import { getAbsenceReasonsRequest, parseAbsenceReasonError } from 'api/requests/absence-reason-requests';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ABSENCE_REASON__LOAD_ERROR } from 'translations/constants';
import { IAbsenceReason } from 'types/absence-reason/IAbsenceReason';
import { getAbsenceReasonErrorKey } from '../utils';

export interface UseAbsenceReasonsOverviewResult {
  rows: IAbsenceReason[];
  hasLoaded: boolean;
  loadErrorKey: string | null;
  reload: () => Promise<void>;
}

/** Save and close can each trigger a reload in quick succession, so only the latest request's answer is applied. */
export const useAbsenceReasonsOverview = (): UseAbsenceReasonsOverviewResult => {
  const [rows, setRows] = useState<IAbsenceReason[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const reload = useCallback(async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    try {
      const reasons = await getAbsenceReasonsRequest();
      if (requestId !== latestRequest.current) return;
      setRows(reasons);
      setLoadErrorKey(null);
      setHasLoaded(true);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      setLoadErrorKey(getAbsenceReasonErrorKey(parseAbsenceReasonError(error), ABSENCE_REASON__LOAD_ERROR));
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rows, hasLoaded, loadErrorKey, reload };
};
